import { useState, useEffect, useMemo } from 'react';
import { Link, router } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import TeacherShell, { TeacherPageHeader } from '../../../../components/layout/TeacherShell';
import { Card, Button, Input, StickyFooter } from '../../../../components/ui';
import { cn } from '../../../../lib/cn';

export default function VocabularyShow({
  course,
  owned_lists,
  assigned_lists,
  available_system_lists,
  searchable_books,
  student_word_counts,
}) {
  const { t } = useTranslation();
  const scrollTitle = (
    <>
      <Link href="/teacher/courses" className="hover:text-blue-600 dark:hover:text-blue-400">{t('teacherCourses.title')}</Link>
      {'  >  '}
      <Link href={`/teacher/courses/${course.id}`} className="hover:text-blue-600 dark:hover:text-blue-400">{course.title}</Link>
      {`  >  ${t('nav.vocabulary')}`}
    </>
  );

  return (
    <TeacherShell scrollTitle={scrollTitle}>
      <TeacherPageHeader
        title={t('nav.vocabulary')}
        subtitle={course.title}
        backLink={`/teacher/courses/${course.id}`}
        backLabel={course.title}
      />

      <div className="space-y-8">
        <AssignedListsSection courseId={course.id} initialAssigned={assigned_lists} initialAvailable={available_system_lists} />
        <CourseListSection courseId={course.id} list={owned_lists[0] || null} />
        <ChapterSuggestions
          courseId={course.id}
          courseList={owned_lists[0] || null}
          searchableBooks={searchable_books}
        />
      </div>
    </TeacherShell>
  );
}

// ── Section: Course Word List ─────────────────────────────

function CourseListSection({ courseId, list }) {
  const { t } = useTranslation();
  const [words, setWords] = useState(list?.words || []);
  const [expandedBooks, setExpandedBooks] = useState({});

  // Sync words when Inertia delivers fresh props (e.g. navigating back to this page)
  useEffect(() => {
    setWords(list?.words || []);
  }, [list]);

  const sortedWordsByBook = useMemo(() => {
    const sorted = [...words].sort((a, b) => (a.frequency_zipf ?? 99) - (b.frequency_zipf ?? 99));
    const groups = {};
    sorted.forEach(w => {
      const book = w.book_title || 'Other';
      if (!groups[book]) groups[book] = [];
      groups[book].push(w);
    });
    return groups;
  }, [words]);

  const toggleBook = (bookTitle) => {
    setExpandedBooks(prev => ({ ...prev, [bookTitle]: !prev[bookTitle] }));
  };

  const handleRemoveWord = async (wordId) => {
    if (!list) return;
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    const res = await fetch(
      `/teacher/courses/${courseId}/vocabulary/lists/${list.id}/words/${wordId}`,
      { method: 'DELETE', headers: { 'X-CSRF-Token': csrfToken } }
    );
    if (res.ok) {
      setWords(prev => prev.filter(w => w.id !== wordId));
    }
  };

  return (
    <section>
      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('teacherCourseVocab.courseWordList')}</h2>
        <span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full">
          {words.length} {t('common.words')}
        </span>
      </div>

      {words.length === 0 ? (
        <Card className="p-8 text-center text-gray-400 dark:text-gray-500">
          {t('teacherCourseVocab.noWordsYet')}
        </Card>
      ) : (
        <div className="space-y-2">
          {Object.entries(sortedWordsByBook).map(([bookTitle, bookWords]) => (
            <Card key={bookTitle} className="overflow-hidden">
              <button
                onClick={() => toggleBook(bookTitle)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="font-medium text-gray-900 dark:text-white">{bookTitle}</span>
                  <span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full">
                    {bookWords.length} {t('common.words')}
                  </span>
                </div>
                <ChevronIcon expanded={!!expandedBooks[bookTitle]} />
              </button>

              {expandedBooks[bookTitle] && (
                <div className="border-t border-gray-100 dark:border-gray-800 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                        <th className="px-4 py-2">{t('dictionary.words')}</th>
                        <th className="px-4 py-2">{t('teacherCourseVocab.pos')}</th>
                        <th className="px-4 py-2">{t('teacherCourseVocab.frequency')}</th>
                        <th className="px-4 py-2">{t('teacherCourseVocab.context')}</th>
                        <th className="px-4 py-2">Cloze</th>
                        <th className="px-4 py-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookWords.map(w => (
                        <tr key={w.id} className="border-t border-gray-50 dark:border-gray-800/50">
                          <td className="px-4 py-2 font-medium text-gray-900 dark:text-white">{w.lemma}</td>
                          <td className="px-4 py-2 text-gray-500 dark:text-gray-400">{w.pos}</td>
                          <td className="px-4 py-2">
                            <FrequencyDot band={w.frequency_band} />
                          </td>
                          <td className="px-4 py-2 text-gray-500 dark:text-gray-400 text-xs max-w-xs">
                            <HighlightedContext sentence={w.context_sentence} lemma={w.lemma} />
                          </td>
                          <td className="px-4 py-2">
                            <ContextSufficiencyToggle
                              courseId={courseId}
                              wordId={w.word_id}
                              insufficient={w.context_sentence_insufficient}
                              hasContext={!!w.context_sentence}
                              onUpdate={(val) => {
                                setWords(prev => prev.map(pw =>
                                  pw.id === w.id ? { ...pw, context_sentence_insufficient: val } : pw
                                ));
                              }}
                            />
                          </td>
                          <td className="px-4 py-2">
                            <button
                              onClick={() => handleRemoveWord(w.id)}
                              className="text-gray-300 dark:text-gray-600 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                              title={t('teacherCourseVocab.removeWord')}
                            >
                              <XIcon className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}

// ── Section 2: Assigned System Lists ─────────────────────────

function AssignedListsSection({ courseId, initialAssigned, initialAvailable }) {
  const { t } = useTranslation();
  const [assigned, setAssigned] = useState(initialAssigned);
  const [available, setAvailable] = useState(initialAvailable);
  const [showPicker, setShowPicker] = useState(false);
  const [filter, setFilter] = useState('');
  const [assigning, setAssigning] = useState(false);

  const filteredAvailable = available.filter(l =>
    l.name.toLowerCase().includes(filter.toLowerCase())
  );

  const handleAssign = async (list) => {
    setAssigning(true);
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const res = await fetch(`/teacher/courses/${courseId}/vocabulary/system_list_assignments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ vocabulary_list_id: list.id }),
      });
      if (res.ok) {
        setAssigned(prev => [...prev, { ...list, words: [] }]);
        setAvailable(prev => prev.filter(l => l.id !== list.id));
        setShowPicker(false);
        setFilter('');
      }
    } catch { /* ignore */ }
    setAssigning(false);
  };

  const handleUnassign = async (assignmentId, listId) => {
    if (!assignmentId) return; // serializer didn't supply one — bail rather than 404
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    const res = await fetch(`/teacher/courses/${courseId}/vocabulary/system_list_assignments/${assignmentId}`, {
      method: 'DELETE',
      headers: { 'X-CSRF-Token': csrfToken },
    });
    if (res.ok) {
      const removed = assigned.find(l => l.id === listId);
      setAssigned(prev => prev.filter(l => l.id !== listId));
      if (removed) {
        setAvailable(prev => [...prev, { id: removed.id, name: removed.name, list_type: removed.list_type, word_count: removed.word_count }]);
      }
    }
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('teacherCourseVocab.assignedLists')}</h2>
        <Button variant="secondary" size="sm" onClick={() => setShowPicker(true)}>
          {t('teacherCourseVocab.assignListPlus')}
        </Button>
      </div>

      {showPicker && (
        <Card className="p-4 mb-4">
          <Input
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder={t('teacherCourseVocab.searchSystemLists')}
            autoFocus
          />
          <div className="mt-2 max-h-48 overflow-y-auto space-y-1">
            {filteredAvailable.length === 0 ? (
              <p className="text-sm text-gray-400 py-2">{t('teacherCourseVocab.noMatchingLists')}</p>
            ) : (
              filteredAvailable.map(list => (
                <button
                  key={list.id}
                  onClick={() => handleAssign(list)}
                  disabled={assigning}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center justify-between disabled:opacity-50"
                >
                  <span className="text-sm text-gray-700 dark:text-gray-300">{list.name}</span>
                  <span className="text-xs text-gray-400">{list.word_count} {t('common.words')}</span>
                </button>
              ))
            )}
          </div>
          <div className="mt-2 flex justify-end">
            <Button variant="ghost" size="sm" onClick={() => { setShowPicker(false); setFilter(''); }}>
              {t('common.cancel')}
            </Button>
          </div>
        </Card>
      )}

      {assigned.length === 0 && !showPicker && (
        <Card className="p-8 text-center text-gray-400 dark:text-gray-500">
          {t('teacherCourseVocab.noSystemLists')}
        </Card>
      )}

      <div className="space-y-2">
        {assigned.map(list => (
          <Card key={list.id} className="px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-900 dark:text-white">{list.name}</span>
              <span className="text-xs text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full">
                {list.word_count} {t('common.words')}
              </span>
              <span className="text-xs text-gray-400 dark:text-gray-500">{list.list_type}</span>
            </div>
            <button
              onClick={() => handleUnassign(list.assignment_id, list.id)}
              className="text-gray-300 dark:text-gray-600 hover:text-red-500 dark:hover:text-red-400 transition-colors p-1"
              title={t('teacherCourseVocab.unassignList')}
            >
              <XIcon className="w-4 h-4" />
            </button>
          </Card>
        ))}
      </div>
    </section>
  );
}

// ── Section 3: Vocabulary from Readings ──────────────────────

function ChapterSuggestions({ courseId, courseList, searchableBooks }) {
  const { t } = useTranslation();
  const [books, setBooks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedBook, setExpandedBook] = useState(null);
  const [expandedChapter, setExpandedChapter] = useState(null);
  const [checked, setChecked] = useState({});
  const [listId, setListId] = useState(courseList?.id || null);
  const [addingWords, setAddingWords] = useState(false);
  const [addProgress, setAddProgress] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [addedWordIds, setAddedWordIds] = useState(new Set());

  useEffect(() => {
    if (courseList?.id) setListId(courseList.id);
  }, [courseList?.id]);

  // Collect all word_ids already in the course list
  const existingWordIds = useMemo(() => {
    const ids = new Set();
    (courseList?.words || []).forEach(w => ids.add(w.word_id));
    return ids;
  }, [courseList]);

  // Merge existing + newly added
  const allAddedWordIds = useMemo(() => {
    const merged = new Set(existingWordIds);
    addedWordIds.forEach(id => merged.add(id));
    return merged;
  }, [existingWordIds, addedWordIds]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/teacher/courses/${courseId}/vocabulary/chapter_suggestions`);
        const data = await res.json();
        if (!cancelled) setBooks(data.books);
      } catch {
        if (!cancelled) setBooks([]);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [courseId]);

  const checkedWords = useMemo(() => {
    if (!books) return [];
    const words = [];
    books.forEach(book => {
      book.chapters.forEach(ch => {
        ch.words.forEach(w => {
          if (checked[w.id]) words.push(w);
        });
      });
    });
    return words;
  }, [books, checked]);

  const toggleChapterAll = (chapter, selectAll) => {
    setChecked(prev => {
      const next = { ...prev };
      chapter.words.forEach(w => {
        if (!allAddedWordIds.has(w.word_id)) {
          next[w.id] = selectAll;
        }
      });
      return next;
    });
  };

  const ensureCourseList = async (csrfToken) => {
    if (listId) return listId;
    const res = await fetch(`/teacher/courses/${courseId}/vocabulary/lists`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ name: 'Course Vocabulary' }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || t('teacherCourseVocab.addFailed'));
    setListId(data.id);
    return data.id;
  };

  const handleAddSelected = async () => {
    if (checkedWords.length === 0) return;
    setAddingWords(true);
    setActionError(null);
    let addedCount = 0;
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    const newlyAdded = new Set();

    try {
      const activeListId = await ensureCourseList(csrfToken);
      for (const [index, w] of checkedWords.entries()) {
        setAddProgress(t('teacherCourseVocab.addingProgress', { current: index + 1, total: checkedWords.length }));
        const res = await fetch(`/teacher/courses/${courseId}/vocabulary/lists/${activeListId}/words`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
          body: JSON.stringify({
            surface_form: w.lemma,
            context_sentence: w.context_sentence,
            source_reference_type: 'BookChapter',
            source_reference_id: w.chapter_id,
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || t('teacherCourseVocab.addFailed'));
        newlyAdded.add(w.word_id);
        if (!data.already_added) addedCount++;
      }

      setAddedWordIds(prev => {
        const merged = new Set(prev);
        newlyAdded.forEach(id => merged.add(id));
        return merged;
      });
      setChecked({});
      setAddProgress(t('teacherCourseVocab.addedCount', { count: addedCount }));
      setTimeout(() => setAddProgress(null), 3000);

      // Refresh Inertia props so CourseListSection picks up new words
      router.reload({ only: ['owned_lists'] });
    } catch (error) {
      if (newlyAdded.size > 0) {
        setAddedWordIds(prev => new Set([...prev, ...newlyAdded]));
        router.reload({ only: ['owned_lists'] });
      }
      setActionError(error.message || t('teacherCourseVocab.addFailed'));
      setAddProgress(null);
    } finally {
      setAddingWords(false);
    }
  };

  return (
    <section>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('teacherCourseVocab.vocabFromReadings')}</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        {t('teacherCourseVocab.vocabFromReadingsDesc')}
      </p>

      <BookContextPicker
        courseId={courseId}
        books={searchableBooks}
        allAddedWordIds={allAddedWordIds}
        ensureCourseList={ensureCourseList}
        onAdded={(wordId) => {
          setAddedWordIds(prev => new Set([...prev, wordId]));
          router.reload({ only: ['owned_lists'] });
        }}
      />

      {actionError && (
        <p role="alert" className="mb-4 text-sm text-red-600 dark:text-red-400">{actionError}</p>
      )}

      {loading && (
        <Card className="p-8 text-center text-gray-400 dark:text-gray-500">
          <span className="inline-block animate-spin mr-2">⟳</span>
          {t('teacherCourseVocab.loadingChapters')}
        </Card>
      )}

      {!loading && (!books || books.length === 0) && (
        <Card className="p-8 text-center text-gray-400 dark:text-gray-500">
          {searchableBooks.length > 0
            ? t('teacherCourseVocab.noChapterSuggestions')
            : t('teacherCourseVocab.noBooksAttached')}
        </Card>
      )}

      {!loading && books && books.length > 0 && (
        <div className="space-y-3">
          {books.map(book => (
            <Card key={book.book_id} className="overflow-hidden">
              <button
                onClick={() => setExpandedBook(expandedBook === book.book_id ? null : book.book_id)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
              >
                <div>
                  <span className="font-medium text-gray-900 dark:text-white">{book.book_title}</span>
                  {book.book_author && (
                    <span className="text-sm text-gray-400 dark:text-gray-500 ml-2">by {book.book_author}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full">
                    {book.chapters.length} {t('teacherCourseShow.chapters')}
                  </span>
                  <ChevronIcon expanded={expandedBook === book.book_id} />
                </div>
              </button>

              {expandedBook === book.book_id && (
                <div className="border-t border-gray-100 dark:border-gray-800">
                  {book.chapters.map(chapter => (
                    <div key={chapter.chapter_id}>
                      <button
                        onClick={() => setExpandedChapter(expandedChapter === chapter.chapter_id ? null : chapter.chapter_id)}
                        className="w-full flex items-center justify-between px-6 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors border-b border-gray-50 dark:border-gray-800/50"
                      >
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {chapter.formatted_label || chapter.chapter_title}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full">
                            {chapter.word_count} {t('teacherCourseVocab.suggestions')}
                          </span>
                          <ChevronIcon expanded={expandedChapter === chapter.chapter_id} />
                        </div>
                      </button>

                      {expandedChapter === chapter.chapter_id && (
                        <ChapterSuggestionPanel
                          courseId={courseId}
                          chapter={chapter}
                          checked={checked}
                          setChecked={setChecked}
                          allAddedWordIds={allAddedWordIds}
                          toggleChapterAll={toggleChapterAll}
                          onSwapped={(vlwId, swapped) => {
                            setBooks(prev => prev.map(b => ({
                              ...b,
                              chapters: b.chapters.map(c => c.chapter_id !== chapter.chapter_id ? c : {
                                ...c,
                                words: c.words.map(w => w.id !== vlwId ? w : {
                                  ...w,
                                  word_id: swapped.word_id,
                                  lemma: swapped.lemma,
                                  pos: swapped.pos,
                                  frequency_band: swapped.frequency_band
                                })
                              })
                            })));
                            router.reload({ only: ['owned_lists'] });
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          ))}

          {/* Bulk add bar */}
          {checkedWords.length > 0 && (
            <StickyFooter>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                  {checkedWords.length} {t('teacherCourseVocab.wordsSelected')}
                </span>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleAddSelected}
                  disabled={addingWords}
                >
                  {t('teacherCourseVocab.addWords')}
                </Button>
                {addProgress && (
                  <span className="text-sm text-gray-500 dark:text-gray-400">{addProgress}</span>
                )}
              </div>
            </StickyFooter>
          )}
        </div>
      )}
    </section>
  );
}

function BookContextPicker({ courseId, books = [], allAddedWordIds, ensureCourseList, onAdded }) {
  const { t } = useTranslation();
  const [bookId, setBookId] = useState(books[0]?.id?.toString() || '');
  const [query, setQuery] = useState('');
  const [result, setResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [addingOccurrenceId, setAddingOccurrenceId] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    if (!books.some(book => book.id.toString() === bookId)) {
      setBookId(books[0]?.id?.toString() || '');
    }
  }, [books, bookId]);

  const handleSearch = async (event) => {
    event.preventDefault();
    if (query.trim().length < 2 || !bookId) return;

    setSearching(true);
    setError(null);
    setNotice(null);
    setResult(null);
    try {
      const params = new URLSearchParams({ library_item_id: bookId, q: query.trim() });
      const response = await fetch(`/teacher/courses/${courseId}/vocabulary/book_context_search?${params}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('teacherCourseVocab.searchFailed'));
      setResult(data);
    } catch (searchError) {
      setError(searchError.message || t('teacherCourseVocab.searchFailed'));
    } finally {
      setSearching(false);
    }
  };

  const handleAdd = async (occurrence) => {
    setAddingOccurrenceId(occurrence.id);
    setError(null);
    setNotice(null);
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const activeListId = await ensureCourseList(csrfToken);
      const response = await fetch(`/teacher/courses/${courseId}/vocabulary/lists/${activeListId}/words`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({
          surface_form: occurrence.lemma,
          context_sentence: occurrence.context_sentence,
          source_reference_type: 'BookChapter',
          source_reference_id: occurrence.chapter_id,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('teacherCourseVocab.addFailed'));

      onAdded(occurrence.word_id);
      setNotice(data.already_added
        ? t('teacherCourseVocab.wordWasAlreadyAdded', { word: occurrence.lemma })
        : t('teacherCourseVocab.contextAdded', { word: occurrence.lemma }));
    } catch (addError) {
      setError(addError.message || t('teacherCourseVocab.addFailed'));
    } finally {
      setAddingOccurrenceId(null);
    }
  };

  if (books.length === 0) return null;

  return (
    <Card className="p-4 mb-6" data-testid="book-context-picker">
      <div className="mb-3">
        <h3 className="font-semibold text-gray-900 dark:text-white">
          {t('teacherCourseVocab.findContext')}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('teacherCourseVocab.findContextDesc')}
        </p>
      </div>

      <form onSubmit={handleSearch} className="flex flex-col gap-2 sm:flex-row">
        {books.length > 1 ? (
          <select
            value={bookId}
            onChange={(event) => { setBookId(event.target.value); setResult(null); setError(null); }}
            aria-label={t('teacherCourseVocab.chooseBook')}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
          >
            {books.map(book => (
              <option key={book.id} value={book.id}>{book.title}</option>
            ))}
          </select>
        ) : (
          <div className="flex items-center rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-600 dark:bg-gray-800 dark:text-gray-300">
            {books[0].title}
          </div>
        )}
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('teacherCourseVocab.searchBookPlaceholder')}
          aria-label={t('teacherCourseVocab.searchBookPlaceholder')}
          data-testid="book-context-search-input"
        />
        <Button type="submit" variant="primary" disabled={searching || query.trim().length < 2}>
          {searching ? t('common.loading') : t('common.search')}
        </Button>
      </form>

      {error && <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
      {notice && <p role="status" className="mt-3 text-sm text-green-700 dark:text-green-400">{notice}</p>}

      {result && result.occurrences.length === 0 && (
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
          {t('teacherCourseVocab.noBookMatches')}
        </p>
      )}

      {result && result.occurrences.length > 0 && (
        <div className="mt-4">
          <div className="mb-2 flex flex-wrap items-baseline gap-2">
            <span className="font-medium text-gray-900 dark:text-white">{result.word.lemma}</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">{result.word.pos}</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {t('teacherCourseVocab.occurrencesFound', { count: result.total_occurrences })}
            </span>
          </div>
          <div className="space-y-2">
            {result.occurrences.map(occurrence => {
              const alreadyAdded = allAddedWordIds.has(occurrence.word_id);
              return (
                <div
                  key={occurrence.id}
                  className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/50"
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <span className="font-medium text-gray-700 dark:text-gray-200">{occurrence.chapter_label}</span>
                        {occurrence.relationship === 'related' && (
                          <span>{t('teacherCourseVocab.relatedForm', { word: occurrence.lemma })}</span>
                        )}
                      </div>
                      <p className="text-sm leading-6 text-gray-700 dark:text-gray-300">
                        <HighlightedContext sentence={occurrence.context_sentence} lemma={occurrence.matched_form} />
                      </p>
                    </div>
                    {alreadyAdded ? (
                      <span className="self-start text-xs text-green-700 dark:text-green-400">
                        {t('teacherCourseVocab.alreadyAdded')}
                      </span>
                    ) : (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleAdd(occurrence)}
                        disabled={addingOccurrenceId !== null}
                      >
                        {addingOccurrenceId === occurrence.id ? '…' : t('teacherCourseVocab.useThisContext')}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
}

function ChapterSuggestionPanel({ courseId, chapter, checked, setChecked, allAddedWordIds, toggleChapterAll, onSwapped }) {
  const { t } = useTranslation();

  return (
    <div className="px-6 py-3 bg-gray-50/50 dark:bg-gray-800/20">
      {/* Select all / Clear */}
      <div className="flex gap-3 mb-3 text-xs">
        <button
          onClick={() => toggleChapterAll(chapter, true)}
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          {t('common.selectAll')}
        </button>
        <button
          onClick={() => toggleChapterAll(chapter, false)}
          className="text-gray-400 hover:underline"
        >
          {t('common.clear')}
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider">
              <th className="px-2 py-1.5 w-8"></th>
              <th className="px-2 py-1.5">{t('teacherCourseVocab.word')}</th>
              <th className="px-2 py-1.5">{t('teacherCourseVocab.pos')}</th>
              <th className="px-2 py-1.5">{t('teacherCourseVocab.frequency')}</th>
              <th className="px-2 py-1.5">{t('teacherCourseVocab.context')}</th>
              <th className="px-2 py-1.5 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {chapter.words.map(w => {
              const alreadyAdded = allAddedWordIds.has(w.word_id);
              return (
                <tr
                  key={w.id}
                  className={cn(
                    'border-t border-gray-100 dark:border-gray-800/50',
                    alreadyAdded && 'opacity-50'
                  )}
                >
                  <td className="px-2 py-2">
                    {alreadyAdded ? (
                      <span className="text-xs text-green-500" title={t('teacherCourseVocab.alreadyAdded')}>✓</span>
                    ) : (
                      <input
                        type="checkbox"
                        checked={!!checked[w.id]}
                        onChange={() => setChecked(prev => ({ ...prev, [w.id]: !prev[w.id] }))}
                      />
                    )}
                  </td>
                  <td className="px-2 py-2 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                    {w.lemma}
                    {' '}
                    <OccurrencePill count={w.occurrences_in_book} familyCount={w.occurrences_in_family_in_book} />
                    {alreadyAdded && (
                      <span className="ml-1.5 text-xs text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-1.5 py-0.5 rounded">
                        {t('teacherCourseVocab.alreadyAdded')}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-gray-500 dark:text-gray-400">{w.pos}</td>
                  <td className="px-2 py-2">
                    <FrequencyDot band={w.frequency_band} />
                  </td>
                  <td className="px-2 py-2 text-gray-500 dark:text-gray-400 text-xs">
                    <HighlightedContext sentence={w.context_sentence} lemma={w.lemma} />
                  </td>
                  <td className="px-2 py-2">
                    <SwapFormButton
                      courseId={courseId}
                      vlwId={w.id}
                      onSwapped={(swapped) => onSwapped(w.id, swapped)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OccurrencePill({ count, familyCount }) {
  if (count == null) return null;
  const showFamily = familyCount != null && familyCount !== count;
  return (
    <span className="text-[10px] text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
      ×{count}{showFamily && <span className="text-gray-400 dark:text-gray-500"> (family ×{familyCount})</span>}
    </span>
  );
}

function SwapFormButton({ courseId, vlwId, onSwapped }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [swapping, setSwapping] = useState(false);
  const [hasForms, setHasForms] = useState(true);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(`/teacher/courses/${courseId}/vocabulary/list_words/${vlwId}/related_forms`);
        if (!res.ok) {
          if (!cancelled) { setData(null); setHasForms(false); }
        } else {
          const json = await res.json();
          if (!cancelled) {
            setData(json);
            setHasForms((json.forms || []).length > 0);
          }
        }
      } catch {
        if (!cancelled) { setData(null); setHasForms(false); }
      }
      if (!cancelled) setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [open, courseId, vlwId]);

  const handleSwap = async (targetWordId) => {
    setSwapping(true);
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const res = await fetch(`/teacher/courses/${courseId}/vocabulary/list_words/${vlwId}/swap_form`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ target_word_id: targetWordId }),
      });
      if (res.ok) {
        const swapped = await res.json();
        onSwapped(swapped);
        setOpen(false);
      }
    } catch { /* swallow */ }
    setSwapping(false);
  };

  if (!hasForms && !open) return null;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="text-xs text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
        title={t('teacherCourseVocab.switchForm')}
      >
        ⇄
      </button>

      {open && (
        <div className="absolute right-0 top-6 z-10 w-56 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-2">
          <div className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 px-1 pb-1">
            {t('teacherCourseVocab.switchForm')}
          </div>
          {loading && (
            <div className="px-2 py-1 text-xs text-gray-400">{t('common.loading')}…</div>
          )}
          {!loading && data && data.forms.length === 0 && (
            <div className="px-2 py-1 text-xs text-gray-400">{t('teacherCourseVocab.noRelatedForms')}</div>
          )}
          {!loading && data && data.forms.map(f => (
            <button
              key={f.word_id}
              onClick={() => handleSwap(f.word_id)}
              disabled={swapping}
              className="w-full flex items-center justify-between gap-2 px-2 py-1.5 text-left rounded hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
            >
              <span className="text-sm text-gray-900 dark:text-white">{f.lemma}</span>
              <span className="text-xs text-gray-400">{f.pos}</span>
            </button>
          ))}
          <button
            onClick={() => setOpen(false)}
            className="w-full mt-1 px-2 py-1 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            {t('common.cancel')}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Shared UI helpers ────────────────────────────────────────

function HighlightedContext({ sentence, lemma }) {
  if (!sentence) return '—';
  if (!lemma) return sentence;

  const escaped = lemma.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`\\b${escaped}\\w*\\b`, 'gi');
  const result = [];
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(sentence)) !== null) {
    if (match.index > lastIndex) {
      result.push(sentence.slice(lastIndex, match.index));
    }
    result.push(<strong key={match.index} className="text-gray-700 dark:text-gray-200">{match[0]}</strong>);
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < sentence.length) {
    result.push(sentence.slice(lastIndex));
  }
  return result.length > 0 ? result : sentence;
}

function FrequencyDot({ band }) {
  const colors = {
    core: 'bg-green-500',
    common: 'bg-blue-500',
    academic: 'bg-purple-500',
    literary: 'bg-amber-500',
    rare: 'bg-red-500',
  };
  return (
    <span className="inline-flex items-center gap-1">
      <span className={cn('w-2 h-2 rounded-full', colors[band] || 'bg-gray-400')} />
      <span className="text-xs text-gray-400">{band}</span>
    </span>
  );
}

function ChevronIcon({ expanded }) {
  return (
    <svg
      className={cn('w-4 h-4 text-gray-400 transition-transform', expanded && 'rotate-180')}
      fill="none" stroke="currentColor" viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function ContextSufficiencyToggle({ courseId, wordId, insufficient, hasContext, onUpdate }) {
  const [checked, setChecked] = useState(!insufficient);
  const [saving, setSaving] = useState(false);

  // No context sentence → not applicable
  if (!hasContext) return <span className="text-xs text-gray-300 dark:text-gray-600">—</span>;

  const handleToggle = async () => {
    const newInsufficient = checked; // toggling from checked (sufficient) to unchecked (insufficient)
    setSaving(true);
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const res = await fetch(
        `/teacher/courses/${courseId}/vocabulary/words/${wordId}/context_sufficiency`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
          body: JSON.stringify({ insufficient: newInsufficient }),
        }
      );
      if (res.ok) {
        setChecked(!checked);
        onUpdate?.(newInsufficient);
      }
    } catch { /* ignore */ }
    setSaving(false);
  };

  return (
    <label
      className={cn(
        'inline-flex items-center gap-1.5 cursor-pointer',
        saving && 'opacity-50 pointer-events-none'
      )}
      title={checked ? 'Context is enough to infer the word (click to flag as insufficient)' : 'Context is insufficient for cloze (click to mark as sufficient)'}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={handleToggle}
        className="w-3.5 h-3.5 rounded border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 focus:ring-0 focus:ring-offset-0"
      />
    </label>
  );
}

function XIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
